import React from 'react';
import { connect } from 'react-redux';
import { Grid, Typography, Button } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import NumberFormat from 'react-number-format';
import { modalsToggle } from '../../../../../actions';
import { SectionWrapper, SectionTitle } from '../index';
import ViewAllBidsModal from './components/ViewAllBidsModal';
import { useParams } from 'react-router-dom';
import { useGetAdditionalBidText } from './hooks';
import AdditionalTextPopup from './components/AdditionalTextPopup';

const styles = {
  marginBottom: {
    marginBottom: '24px'
  },
  tableRow: {
    paddingBottom: '16px',
    flexWrap: 'nowrap'
  },
  tableContainer: {
    maxHeight: '140px',
    overflowY: 'scroll'
  }
};

const FirstBid = styled.span`
  color: #009f0f;
  font-weight: 500;
`;

const SecondBid = styled.span`
  color: #d50808;
  font-weight: 500;
`;

const WinnerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(253, 183, 20, 0.3);
`;

const StyledTextWithoutOverflow = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  color: #666666;
  font-size: 14px;

  & * p {
    line-height: normal;
  }
`;

const ViewBidsSection = styled.div`
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.div`
  cursor: pointer;
`;

const BidHistoryWrapper = styled.div`
  padding: 21px 25px 21px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ArrowDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 15.6016L5.97501 9.60156H18.025L12 15.6016Z" fill="#757575" />
  </svg>
);

const BidsTable = ({ classes, bids, type, auctionType, startingBid }) => {
  const getAdditionalBidText = useGetAdditionalBidText(auctionType, startingBid);
  if (!bids.length) return null;

  return (
    <Grid container direction="column">
      {bids
        .sort((a, b) => {
          if (a.bid >= b.bid && a.id > b.id) {
            return -1;
          }
          if (b.bid >= a.bid && b.id > a.id) {
            return 1;
          }
          return 0;
        })
        .map((bid, index, array) => {
          const previousBid = array[index - 1];
          const nextBid = array[index + 1];
          if (previousBid && previousBid.bid === bid.bid) {
            return {
              ...bid,
              isSecond: true
            };
          }
          if (nextBid && nextBid.bid === bid.bid) {
            return {
              ...bid,
              isFirst: true
            };
          }
          return bid;
        })
        .map((bid, index) => {
          const { color, text } = getAdditionalBidText(bid);
          return (
            <Grid container classes={{ container: classes.tableRow }} style={{ gap: '8px' }} item xs={12} key={index}>
              <Grid style={{ flexWrap: 'nowrap', display: 'flex' }} item>
                <StyledTextWithoutOverflow>
                  <NumberFormat displayType="text" prefix="$" value={bid.bid} thousandSeparator />
                </StyledTextWithoutOverflow>
                {bid.isFirst && <FirstBid> (1st)</FirstBid>}
                {bid.isSecond && <SecondBid> (2nd)</SecondBid>}
              </Grid>
              <Grid item container wrap="nowrap" alignItems="center" style={{ flex: '1 1 300%' }}>
                <StyledTextWithoutOverflow
                  style={{ lineHeight: 'normal', display: 'flex', gap: 4, width: 'unset' }}
                  title={bid.bidder}
                >
                  {`by ${bid.bidder}`}
                </StyledTextWithoutOverflow>
                {text ? <AdditionalTextPopup text={text} color={color} /> : ''}
              </Grid>
              <Grid item style={{ justifyContent: 'flex-end', flex: '1 1 300%' }} container>
                <StyledTextWithoutOverflow style={{ lineHeight: 'normal' }}>{bid.date}</StyledTextWithoutOverflow>
              </Grid>
            </Grid>
          );
        })}
    </Grid>
  );
};

const labelsConfig = {
  bids: {
    header: 'Bid History',
    itemType: 'Bids',
    modalId: 'view-all-bids',
    noDataText: 'There are no bids yet.'
  },
  offers: {
    header: 'Offers History',
    itemType: 'Offers',
    modalId: 'view-all-offers',
    noDataText: 'There are no offers yet.'
  },
  alternativeOffers: {
    header: 'Offers History',
    itemType: 'Offers',
    modalId: 'view-all-alternative-offers',
    noDataText: 'There are no offers yet.'
  }
};

const BidHistorySection = ({
  classes,
  bids,
  proxyBid,
  toggleModal,
  type,
  initiallyCollapsed,
  auctionType,
  collapsable = true,
  editable = true,
  startingBid = 100,
  awardInfo
}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);

  React.useEffect(() => {
    if (!collapsable) return;
    if (initiallyCollapsed) {
      setIsExpanded(false);
    }
  }, [initiallyCollapsed]);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const { header, itemType, modalId, noDataText } = labelsConfig[type];

  const { id: vehicleId } = useParams();

  return (
    <BidHistoryWrapper>
      <Grid container justify="space-between" alignItems="center" item xs={12}>
        <SectionTitle noGutterBottom>
          <span style={{ fontSize: '16px', fontWeight: 600 }}>
            {header}
            {bids.length ? ` (${bids.length} ${itemType})` : null}
          </span>
        </SectionTitle>
        {bids.length > 0 && (
          <ViewBidsSection>
            <Typography>
              <Button
                style={{ textTransform: 'none', color: '#3994DE' }}
                size="small"
                color="primary"
                onClick={() => toggleModal(modalId)}
              >
                View All
              </Button>
            </Typography>
            {collapsable && (
              <IconWrapper onClick={toggleExpanded}>
                <ArrowDownIcon />
              </IconWrapper>
            )}
          </ViewBidsSection>
        )}
      </Grid>
      {isExpanded && awardInfo && (
        <WinnerRow>
          <StyledTextWithoutOverflow style={{ fontWeight: 600 }}>
            <NumberFormat displayType="text" prefix="$" value={awardInfo.amount} thousandSeparator />
          </StyledTextWithoutOverflow>
          <StyledTextWithoutOverflow style={{ fontWeight: 600, maxWidth: 'unset' }}>
            {`Winner: ${awardInfo.buyerName}`}
          </StyledTextWithoutOverflow>
        </WinnerRow>
      )}
      {isExpanded &&
        (bids.length ? (
          <Grid item xs={12} classes={{ item: classes.tableContainer }}>
            <BidsTable auctionType={auctionType} startingBid={startingBid} type={type} classes={classes} bids={bids} />
          </Grid>
        ) : (
          <Grid item xs={12} style={{ color: 'rgb(97, 97, 97)' }}>
            <StyledTextWithoutOverflow>{noDataText}</StyledTextWithoutOverflow>
          </Grid>
        ))}
      <ViewAllBidsModal
        vehicleId={Number(vehicleId)}
        bids={bids}
        type={type}
        auctionType={auctionType}
        proxyBid={proxyBid}
        editable={editable}
        startingBid={startingBid}
        form={`adminEditBidModalForm-${type}`}
      />
    </BidHistoryWrapper>
  );
};
const mapDispatchToProps = dispatch => ({
  toggleModal: modalId => dispatch(modalsToggle(modalId))
});

export default connect(null, mapDispatchToProps)(withStyles(styles)(BidHistorySection));
